import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, BarChart3, ListChecks, Receipt, LogOut, Copy, Trash2, Loader2, ChevronRight, ChevronLeft, X } from "lucide-react";
import { api, clearSession, errMsg, faDigits, faNum, fmtJalaliDate, fmtJalaliDateTime, timeAgo } from "../lib/api";
import { SERVICE_LABEL, SOURCE_SERVICE, STATUS_LABEL, WINDOW_OPTIONS, countByService } from "../lib/portalLabels";
import InvoiceView from "../components/InvoiceView";

const TABS = [
  { key: "stats", label: "آمار", icon: BarChart3 },
  { key: "requests", label: "درخواست‌ها", icon: ListChecks },
  { key: "tokens", label: "توکن API", icon: KeyRound },
  { key: "invoices", label: "پیش‌فاکتورها", icon: Receipt },
];

const cardCls = "bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5";

export default function UserPanel() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("stats");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data.user)).catch(() => {});
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearSession();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" dir="rtl">
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
        <div>
          <div className="font-bold">پنل کاربری</div>
          <div className="text-xs text-slate-500" dir="ltr">{me ? faDigits(me.mobile) : ""} {me?.name ? `· ${me.name}` : ""}</div>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-600 hover:text-rose-600">
          <LogOut size={16} /> خروج
        </button>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-8">
        <nav className="flex gap-2 overflow-x-auto mb-6 pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm whitespace-nowrap transition-all ${
                  tab === t.key ? "bg-teal-600 text-white shadow" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                }`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </nav>

        {tab === "stats" && <StatsSection />}
        {tab === "requests" && <RequestsSection />}
        {tab === "tokens" && <TokensSection />}
        {tab === "invoices" && <InvoicesSection />}
      </main>
    </div>
  );
}

// ---------------- آمار ----------------
function StatsSection() {
  const [windowKey, setWindowKey] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/portal/stats", { params: { window: windowKey } }).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [windowKey]);

  const services = data ? countByService(data.by_source) : { nid: 0, shahkar: 0, sana: 0 };
  const maxDay = data ? Math.max(1, ...data.by_day.map((d) => d.count)) : 1;

  return (
    <div className="space-y-5">
      <WindowPicker value={windowKey} onChange={setWindowKey} />
      {!data ? (
        <div className="grid place-items-center py-16 text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="کل فراخوانی‌ها" value={data.total} />
            <StatCard title="پاسخ قطعی" value={data.answered} />
            <StatCard title="ثبت احوال" value={services.nid} />
            <StatCard title="شاهکار / سنا" value={services.shahkar + services.sana} />
          </div>
          <div className={cardCls}>
            <div className="font-medium mb-4">فراخوانی روزانه</div>
            {data.by_day.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">در این بازه فراخوانی ثبت نشده است</div>
            ) : (
              <div className="flex items-end gap-1 h-40 overflow-x-auto">
                {data.by_day.map((d) => (
                  <div key={d.date} className="flex flex-col items-center min-w-[18px] flex-1" title={`${fmtJalaliDate(new Date(d.date).getTime() / 1000)}: ${d.count}`}>
                    <div className="w-full rounded-t bg-teal-500/80" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: 2 }} />
                    <div className="text-[10px] text-slate-400 mt-1">{faDigits(d.date.slice(8))}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className={cardCls}>
      <div className="text-xs text-slate-500">{title}</div>
      <div className="text-2xl font-bold mt-2">{faNum(value)}</div>
    </div>
  );
}

function WindowPicker({ value, onChange }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {WINDOW_OPTIONS.map((w) => (
        <button
          key={w.value}
          onClick={() => onChange(w.value)}
          className={`px-3 py-1.5 rounded-lg text-xs border ${value === w.value ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

// ---------------- درخواست‌ها ----------------
function RequestsSection() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [windowKey, setWindowKey] = useState("all");
  const [tokenId, setTokenId] = useState("");
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    api.get("/portal/tokens").then(({ data }) => setTokens(data.items)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/portal/requests", { params: { page, page_size: pageSize, window: windowKey, token_id: tokenId } })
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [page, windowKey, tokenId]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className={cardCls}>
      <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
        <div className="font-medium">درخواست‌های ارسالی ({faNum(total)})</div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={tokenId} onChange={(e) => { setPage(1); setTokenId(e.target.value); }} className="text-xs border rounded-lg px-2 py-1.5 bg-white">
            <option value="">همه توکن‌ها</option>
            {tokens.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={windowKey} onChange={(e) => { setPage(1); setWindowKey(e.target.value); }} className="text-xs border rounded-lg px-2 py-1.5 bg-white">
            {WINDOW_OPTIONS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 border-b">
              <th className="py-2 text-right font-medium">زمان</th>
              <th className="py-2 text-right font-medium">کد ملی</th>
              <th className="py-2 text-right font-medium">سرویس</th>
              <th className="py-2 text-right font-medium">وضعیت</th>
              <th className="py-2 text-right font-medium">زمان پاسخ</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="py-8 text-center"><Loader2 className="animate-spin inline" size={18} /></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">درخواستی یافت نشد</td></tr>}
            {!loading && items.map((r, i) => (
              <tr key={`${r.ts}-${i}`} className="border-b border-slate-100">
                <td className="py-2 whitespace-nowrap">{fmtJalaliDateTime(r.ts)}</td>
                <td className="py-2 font-mono" dir="ltr">{r.national_code_masked || "—"}</td>
                <td className="py-2">{SERVICE_LABEL[SOURCE_SERVICE[r.source]] || "—"}</td>
                <td className="py-2">{STATUS_LABEL[r.status] || "ناموفق / خطا"}</td>
                <td className="py-2 text-slate-500">{faNum(r.duration_ms)} ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex items-center gap-1 disabled:text-slate-300"><ChevronRight size={16} /> قبلی</button>
        <span className="text-slate-500">صفحه {faDigits(page)} از {faDigits(pages)}</span>
        <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 disabled:text-slate-300">بعدی <ChevronLeft size={16} /></button>
      </div>
    </div>
  );
}

// ---------------- توکن‌ها ----------------
function TokensSection() {
  const [items, setItems] = useState([]);
  const [maxTokens, setMaxTokens] = useState(0);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null); // توکن تازه (فقط یک‌بار)
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/portal/tokens").then(({ data }) => { setItems(data.items); setMaxTokens(data.max_tokens); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = items.filter((t) => !t.revoked).length;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/portal/tokens", { name });
      setCreated(data.token);
      setName("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (t) => {
    if (!window.confirm(`توکن «${t.name}» باطل شود؟ این کار برگشت‌ناپذیر است.`)) return;
    try {
      await api.delete(`/portal/tokens/${t.id}`);
      toast.success("توکن باطل شد");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("کپی شد"); }
    catch { toast.error("کپی انجام نشد؛ دستی کپی کنید"); }
  };

  return (
    <div className="space-y-5">
      <div className={cardCls}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium">ساخت توکن جدید</div>
          <div className="text-xs text-slate-500">{faDigits(active)} از {faDigits(maxTokens)} توکن فعال</div>
        </div>
        <form onSubmit={create} className="flex gap-2 flex-wrap">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="نام توکن (مثلاً سرور اصلی)"
            className="flex-1 min-w-[200px] border rounded-xl px-3 py-2 text-sm"
            maxLength={100}
          />
          <button disabled={busy || active >= maxTokens} className="bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white text-sm px-4 py-2 rounded-xl flex items-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} ساخت توکن
          </button>
        </form>
        {created && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="text-sm font-medium text-amber-800 mb-2">توکن جدید شما (فقط همین یک بار نمایش داده می‌شود)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all text-xs bg-white border rounded-lg p-2" dir="ltr">{created}</code>
              <button onClick={() => copy(created)} className="p-2 rounded-lg bg-white border hover:bg-slate-50"><Copy size={16} /></button>
              <button onClick={() => setCreated(null)} className="p-2 rounded-lg bg-white border hover:bg-slate-50"><X size={16} /></button>
            </div>
          </div>
        )}
      </div>

      <div className={cardCls}>
        <div className="font-medium mb-3">توکن‌های من</div>
        {items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">توکنی ساخته نشده است</div>}
        <div className="divide-y divide-slate-100">
          {items.map((t) => (
            <div key={t.id} className="py-3 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="font-medium text-sm">
                  {t.name} {t.revoked && <span className="text-xs text-rose-600">(باطل شده)</span>}
                </div>
                <div className="text-xs text-slate-500 font-mono" dir="ltr">{t.prefix}</div>
              </div>
              <div className="text-xs text-slate-500 space-x-3 space-x-reverse">
                <span>ساخت: {fmtJalaliDate(t.created_at)}</span>
                <span>آخرین استفاده: {t.last_used_at ? timeAgo(t.last_used_at) : "هرگز"}</span>
                <span>فراخوانی: {faNum(t.total_calls)}</span>
              </div>
              {!t.revoked && (
                <button onClick={() => revoke(t)} className="text-rose-600 hover:bg-rose-50 p-2 rounded-lg flex items-center gap-1 text-xs">
                  <Trash2 size={14} /> ابطال
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- پیش‌فاکتورها ----------------
function InvoicesSection() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/portal/invoices").then(({ data }) => setItems(data.items)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const view = async (id) => {
    try {
      const { data } = await api.get(`/portal/invoices/${id}`);
      setOpen(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (open) {
    return (
      <div className="space-y-3">
        <button onClick={() => setOpen(null)} className="text-sm text-teal-700 flex items-center gap-1"><ChevronRight size={16} /> بازگشت به لیست</button>
        <InvoiceView invoice={open} />
      </div>
    );
  }

  return (
    <div className={cardCls}>
      <div className="font-medium mb-3">پیش‌فاکتورهای صادرشده</div>
      {items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">پیش‌فاکتوری صادر نشده است</div>}
      <div className="divide-y divide-slate-100">
        {items.map((i) => (
          <button key={i.id} onClick={() => view(i.id)} className="w-full py-3 flex justify-between text-sm hover:bg-slate-50 px-2 rounded-lg">
            <span>شماره {faDigits(i.invoice_no)} · {fmtJalaliDate(i.period_start)} تا {fmtJalaliDate(i.period_end - 1)}</span>
            <span className="font-medium">{faNum(i.total)} ریال</span>
          </button>
        ))}
      </div>
    </div>
  );
}
